import { useCallback, useEffect, useState } from 'react';
import {
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  NACE_CODE,
  NACE_SECTION,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BLOCKER_STATES } from 'constants/general';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import { getCreateCustomerPageMockValues } from 'mocks/dataSetsCreatePageMocks';
import {
  useCustomerCreateMutation, useCustomerRecordQuery,
  useCustomerRecordUpdateMutation,
  useEnumQuery, useGetNaceCodeTreeQuery, useUidNameTuplesQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes, selectUidNameTuples } from 'store/slices/dataSets/selectors';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForCustomer from 'utils/formHelpers/getRequestDataForCustomer';

import useCreateCustomerForm from './hooks/useCreateCustomerForm';
import CreateCustomerView from './CreateCustomerView';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type IDataSetsOutletContext from 'layout/DataSetsLayout/interfaces/IDataSetsOutletContext';
import type { ReactElement } from 'react';
import type { Path, PathValue } from 'react-hook-form';
import type ICustomerRecordRequest from 'store/slices/dataSets/interfaces/ICustomerRecordRequest';
import type { TCreateCustomerSchema } from 'utils/validators';

function CreateCustomer(): ReactElement {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const { setTitleAction } = useOutletContext<IDataSetsOutletContext>();

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const [customerCreate, { isLoading: isCustomerCreateLoading }] = useCustomerCreateMutation();

  const [customerRecordUpdate, { isLoading: isCustomerRecordUpdate }] = useCustomerRecordUpdateMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const { data: uidNameTuples, isLoading: isNaceUidNameTuplesLoading } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased,
      DATA_SETS_ENUM_TYPES.upstreamTier, DATA_SETS_ENUM_TYPES.priorityRating],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const fetchQuery = recordId ? {
    recordId,
  } : skipToken;
  const { isLoading: isCustomerRecordLoading } = useCustomerRecordQuery(fetchQuery);

  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading || isCustomerCreateLoading || isCustomerRecordUpdate
      || isNaceUidNameTuplesLoading || isCustomerRecordLoading;

  const CustomerEndpoint = recordId ? customerRecordUpdate : customerCreate;
  const {
    control,
    handleSubmit,
    isDirty,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  } = useCreateCustomerForm({ recordId, isPageLoading });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty
        && currentLocation.pathname !== nextLocation.pathname
        && !isConfirmationModalOpen);

  useEffect(() => {
    if (blocker.state === BLOCKER_STATES.blocked) {
      if (isDirty) {
        setIsConfirmationModalOpen(true);
      } else {
        blocker.proceed?.();
      }
    }
  }, [blocker, isDirty]);

  useScrollToError({ formState });

  const onCreateCustomerSubmit = handleSubmit((values) => {
    const requestData: ICustomerRecordRequest = getRequestDataForCustomer({ values, recordId, companyCurrency });

    CustomerEndpoint(requestData).unwrap()
      .then(
        () => {
          toast.success(i18n.t(
            recordId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });
          navigate(AppRoutes.customers);
        },
      ).catch((errors) => {
        setFieldsErrors<TCreateCustomerSchema>({ errors, setError, fieldsName });
      });
  });

  const onHandleCloseConfirmation = (isOpen = false) => {
    if (!isConfirmationModalOpen && !isDirty) {
      navigate(-1);
    } else {
      setIsConfirmationModalOpen(isOpen);
    }
  };

  const onDiscardChangesConfirm = () => {
    const currentLocation = blocker?.location?.pathname;
    if (currentLocation) {
      navigate(currentLocation);
    } else {
      navigate(-1);
    }
    reset(undefined, { keepValues: true });
  };

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateCustomerSchema, Path<TCreateCustomerSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateCustomerSchema>({ watch, setValue });

  const { dateOpened, onDateOpenedChange, startDate } = useDataSetsDate<TCreateCustomerSchema>({ watch, setValue });

  const onChangeDate = (date: TDate, onChange: (date: TDate) => void) => {
    onChange(date);
    onDateOpenedChange(date);
  };

  const isMockFillVisible = !recordId && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = useCallback(() => {
    const valueChainListOption = watch(VALUE_CHAIN_LIST);
    const valueChainSectionOption = watch(VALUE_CHAIN_SECTION);

    reset({
      ...watch(),
      ...getCreateCustomerPageMockValues({
        enumData,
        naceCodesData,
        profitCenterOptions: profitCenterIds?.[UID_NAME_TUPLES.profitCenter],
        countryOptions: defaultCountries,
      }),
      [VALUE_CHAIN_LIST]: valueChainListOption?.value ? valueChainListOption : vcOptionsList?.[0],
      [VALUE_CHAIN_SECTION]: valueChainSectionOption?.value ? valueChainSectionOption : listPosition?.[0],
    });
  }, [defaultCountries, enumData, listPosition, naceCodesData, profitCenterIds, reset, vcOptionsList, watch]);

  useEffect(() => {
    if (!isMockFillVisible) {
      setTitleAction(null);
      return undefined;
    }

    setTitleAction(
      <ModalMockFillButton
        onClick={onFillMockData}
        disabled={isPageLoading}
      />,
    );

    return () => {
      setTitleAction(null);
    };
  }, [isMockFillVisible, isPageLoading, onFillMockData, setTitleAction]);

  return (
    <CreateCustomerView
      control={control}
      onCreateCustomerSubmit={onCreateCustomerSubmit}
      onHandleCloseConfirmation={onHandleCloseConfirmation}
      isConfirmationModalOpen={isConfirmationModalOpen}
      onDiscardChangesConfirm={onDiscardChangesConfirm}
      enumData={enumData}
      naceCodesData={naceCodesData}
      naceDivisions={naceDivisions}
      onNaceSelectionChange={onNaceSelectionChange}
      isPageLoading={isPageLoading}
      onFindCountries={onFindCountries}
      onFindStates={onFindStates}
      onFindCities={onFindCities}
      isStatesDisabled={isStatesDisabled}
      isCitiesDisabled={isCitiesDisabled}
      uidNameTuples={uidNameTuples}
      onChangeCountry={onChangeCountry}
      onChangeState={onChangeState}
      dateOpened={dateOpened}
      startDate={startDate}
      onChangeDate={onChangeDate}
      profitCenterIds={profitCenterIds}
      watch={watch}
      setValue={setValue}
      defaultCountries={defaultCountries}
      handleGetVcOptions={handleGetVcOptions}
      vcOptionsList={vcOptionsList}
      listPosition={listPosition}
    />
  );
}

export default CreateCustomer;
